"use client";

import type { HTMLAttributes, MouseEvent, ReactNode, FC, Ref } from "react";
import { useTranslation } from "react-i18next";
import { mergeClasses } from "@huub-web/utils";

import { Card, type CardProps } from "../Card";
import type { ClassNameProp } from "../../props";
import type { HouseRecord } from "../../types";
import { IconButton } from "../IconButton";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { ValueParser } from "../ValueParser";
import { Chip } from "../Chip";
import { Tooltip } from "../Tooltip";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { ProgressBar } from "../ProgressBar";
import { Image } from "../Image";
import { Spacing } from "../Spacing";
import { HouseSummary } from "./components/HouseSummary";

import {
  baseHeaderContainerClassName,
  chipStyle,
  contentContainerStyle,
  contentStyle,
  footerStyle,
  headerContentStyle,
  headerImageStyle,
  headerTitleStyle,
  optionsStyle,
  summaryContainerStyle,
  summaryStyle,
} from "./HouseCard.css";

export type HouseCardProps = HTMLAttributes<HTMLDivElement> &
  Pick<
    CardProps,
    | "elevated"
    | "disabled"
    | "border"
    | "trailing"
    | "leading"
    | "padding"
    | "gap"
  > &
  ClassNameProp & {
    interactive?: boolean;
    record: HouseRecord;
    savable?: boolean;
    saved?: boolean;
    notListed?: boolean;
    imageChildren?: ReactNode;
    showSaleState?: boolean;
    buttonValue?: string;
    loading?: boolean;
    imageClassName?: string;
    cardVariant?: "primary" | "tertiary";
    variant?: "default" | "compact";
    summary?: boolean;
    headerTrailing?: ReactNode;
    headerContainerClassName?: string;
    imageHeight?: "l";
    contentTrailing?: ReactNode;
    onClick?: (e: MouseEvent<HTMLElement>, id?: string) => void;
    onSaveToggle?: () => void;

    /**
     * @deprecated
     */
    link?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A simple house card component that returns an empty card.
 * Built on top of the Card component.
 */
export const HouseCard: FC<HouseCardProps> = ({
  interactive = false,
  className,
  record,
  showSaleState,
  variant = "default",
  notListed,
  savable,
  saved,
  loading,
  imageChildren,
  disabled,
  elevated,
  border,
  cardVariant = "primary",
  buttonValue,
  imageHeight,
  headerContainerClassName,
  headerTrailing,
  contentTrailing,
  padding = "s",
  onSaveToggle,
  onClick,
  imageClassName,
  children,
  ref,
  ...props
}) => {
  const { t } = useTranslation();

  // @deprecated
  variant;

  return (
    <Card
      ref={ref}
      as={interactive ? "button" : "div"}
      className={mergeClasses(className)}
      contentClassName={contentStyle}
      variant={cardVariant as CardProps["variant"]}
      border={border}
      elevated={elevated}
      padding={padding}
      {...props}
    >
      {loading && <ProgressBar variant="infinite" position="absolute" />}

      <Flex className={contentContainerStyle}>
        <Image
          src={record.image}
          className={mergeClasses(
            headerImageStyle({ height: imageHeight }),
            imageClassName,
          )}
        >
          {imageChildren}
        </Image>

        <div
          className={
            headerContainerClassName
              ? headerContainerClassName
              : baseHeaderContainerClassName
          }
        >
          <Flex justify="space-between" gap="xs" width="stretch" align="center">
            <Flex
              className={headerContentStyle}
              gap="xs"
              direction="column"
              grow
              {...props}
            >
              <Flex gap="s">
                <Flex direction="column" grow>
                  <Text
                    as="h2"
                    variant="bodyL"
                    className={headerTitleStyle}
                    ellipsis
                  >
                    {record.street}
                  </Text>
                  <Text as="h4" variant="bodyM" color="currentColor50" ellipsis>
                    {record.city}
                  </Text>
                </Flex>

                {savable && (
                  <Flex className={optionsStyle}>
                    <Tooltip
                      value={
                        saved
                          ? t("houseCard.removeHouse")
                          : t("houseCard.saveHouse")
                      }
                      placement="left"
                    >
                      <IconButton
                        icon={saved ? "delete" : "floppyDisk"}
                        size="inline"
                        rounded
                        variant="opaque"
                        border="default"
                        onClick={() => onSaveToggle?.()}
                        disabled={disabled}
                        data-id={saved ? "house-delete" : "house-save"}
                      />
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
              {record.price === 0 || !record.price ? (
                <Chip
                  leading={<Icon icon="infoFilled" size="s2" />}
                  size="l"
                  variant="tertiary"
                  className={chipStyle}
                >
                  {t("houseCard.noValue")}
                </Chip>
              ) : (
                <Flex gap="xs" align="center" wrap>
                  <Text variant="bodyL">
                    <ValueParser
                      value={{ type: "money", value: record.price }}
                    />{" "}
                    {!!record.saleType &&
                      t(`object.saleType.${record.saleType}`)}
                  </Text>
                  {notListed && (
                    <Chip variant="tertiary">
                      {t("houseCard.lastKnownValue")}
                    </Chip>
                  )}
                </Flex>
              )}
              {showSaleState && record.saleState && (
                <div>
                  <Chip elevated variant="secondary">
                    {t(`object.saleState.${record.saleState}`)}
                  </Chip>
                </div>
              )}

              <Flex gap="s" className={footerStyle}>
                {variant === "default" && (
                  <div className={summaryContainerStyle}>
                    <Flex className={summaryStyle}>
                      <HouseSummary
                        color="currentColor50"
                        record={record}
                        textVariant="bodyS"
                        iconSize="s"
                      />
                    </Flex>
                  </div>
                )}

                {!!onClick && (
                  <div>
                    <Button
                      variant="primary"
                      size="s"
                      onClick={(e) => onClick?.(e, record.id)}
                      disabled={disabled}
                    >
                      {buttonValue ?? t("houseCard.viewHouse")}
                    </Button>
                  </div>
                )}
              </Flex>
              {contentTrailing}
            </Flex>
          </Flex>

          {headerTrailing}
        </div>
      </Flex>

      {children && (
        <>
          <Spacing />
          {children}
        </>
      )}
    </Card>
  );
};
